export type SoundEffect = "start" | "shuffle" | "select" | "next" | "end";

export class SoundEffectPlayer {
  private context?: AudioContext;
  private lastShuffleAt = 0;

  play(effect: SoundEffect): void {
    const context = this.ensureContext();
    if (context.state === "suspended") {
      context.resume().catch(() => undefined);
    }

    if (effect === "shuffle") {
      const now = context.currentTime;
      if (now - this.lastShuffleAt < 0.18) return;
      this.lastShuffleAt = now;
      this.playNoiseSweep(0.05, 580, 1700, 0.11);
      this.playNoiseSweep(0.035, 340, 980, 0.16, 0.08);
      return;
    }

    if (effect === "select") {
      this.playTone(523.25, 0.05, 0.055, "triangle");
      this.playTone(783.99, 0.045, 0.12, "sine", 0.045);
      return;
    }

    if (effect === "next") {
      this.playTone(392, 0.04, 0.06, "sine");
      this.playTone(587.33, 0.04, 0.09, "sine", 0.05);
      return;
    }

    if (effect === "end") {
      this.playTone(440, 0.05, 0.08, "triangle");
      this.playTone(329.63, 0.045, 0.12, "triangle", 0.08);
      this.playTone(261.63, 0.04, 0.16, "triangle", 0.18);
      return;
    }

    this.playTone(329.63, 0.045, 0.08, "triangle");
    this.playTone(493.88, 0.045, 0.1, "triangle", 0.07);
    this.playTone(659.25, 0.04, 0.12, "sine", 0.15);
  }

  private ensureContext(): AudioContext {
    if (!this.context) this.context = new AudioContext();
    return this.context;
  }

  private playTone(
    frequency: number,
    volume: number,
    duration: number,
    type: OscillatorType,
    delay = 0
  ): void {
    const context = this.ensureContext();
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
  }

  private playNoiseSweep(volume: number, fromFrequency: number, toFrequency: number, duration: number, delay = 0): void {
    const context = this.ensureContext();
    const start = context.currentTime + delay;
    const buffer = context.createBuffer(1, Math.max(1, Math.floor(context.sampleRate * duration)), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) {
      data[index] = Math.random() * 2 - 1;
    }

    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    source.buffer = buffer;
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(fromFrequency, start);
    filter.frequency.exponentialRampToValueAtTime(toFrequency, start + duration);
    filter.Q.value = 1.8;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    source.start(start);
    source.stop(start + duration + 0.02);
  }
}
